import type { DeveloperState } from './type'
import type { DeveloperInfo } from '@/service/types/login'
import { defineStore } from 'pinia'
import { DeveloperAvatarBgColor, IsSubAccountConst, CertificationStatus } from '@/constant'
import { getRandomNum } from '@/utils/tool'
import { reqGetDeveloperInfo, requestLogout, requestGetExamineStatus } from '@/service/apis'
import useCertificationInfoStore from '@/stores/certificationInfoStore'
import mittBus from '@/utils/mitt'
import UnverifiedAvatar from '@/assets/imgs/common/unverified_avatar.png'

const useDeveloperStore = defineStore('developer', {
  state(): DeveloperState {
    return {
      developerLoginInfo: {
        isSubDeveloper: -1, // 默认不是子账号
        developerId: '',
        accountPower: [],
        developerMobile: '',
        developerEmail: '',
        developerPwd: '',
        examineAllState: 0 // 资质认证审核总状态
      },
      developerAvatar: { avatarText: '', avatarBg: '', avatarBgColor: 'transparent' },
      developerContactInfo: {
        developerName: '', // 联系人姓名
        contactPersonEmail: '', // 联系人邮箱
        identityCardNum: '', // 联系人身份证号
        identityCardPic: [], // 联系人身份证照片
        developerAddress: '', //开发者地址省市
        developerAddInfo: '' //开发者详细地址
      }
    }
  },
  getters: {
    developerId({ developerLoginInfo }) {
      return developerLoginInfo.developerId
    },
    isLoggedIn({ developerLoginInfo }) {
      return window.isInternational
        ? !!developerLoginInfo.developerId && !!developerLoginInfo.developerEmail
        : !!developerLoginInfo.developerId && !!developerLoginInfo.developerMobile
    },
    isSubDeveloper({ developerLoginInfo }) {
      return developerLoginInfo.isSubDeveloper
    },
    avatarBgColor({ developerAvatar: { avatarBgColor } }) {
      return avatarBgColor ?? 'transparent'
    },
    developerMobile({ developerLoginInfo }) {
      return developerLoginInfo.developerMobile
    },
    examineAllState({ developerLoginInfo }) {
      return developerLoginInfo.examineAllState
    },
    accountPower({ developerLoginInfo }) {
      return developerLoginInfo.accountPower
    },
    developerEmail({ developerLoginInfo }) {
      return developerLoginInfo.developerEmail
    },
    developerPwd({ developerLoginInfo }) {
      return developerLoginInfo.developerPwd
    }
  },
  actions: {
    /**
     * 获取开发者信息
     */
    async getDeveloperInfo() {
      const {
        data: { developerInfo, errormsg, errorno }
      } = await reqGetDeveloperInfo({})
      if (errorno !== 0) {
        MessagePlugin('error', errormsg)
        return { errorno }
      }
      console.log('查看开发者信息数据', developerInfo)
      this.setDeveloperContactInfo(developerInfo)
      this.setDeveloperLoginInfo(developerInfo)
      this.setDeveloperAvatar()
      return true
    },

    /**
     * 获取开发者资质审核状态（注意：仅主账号登录情况下才获取成功）
     */
    async getLatestCertificationStatus() {
      if (this.isSubDeveloper === IsSubAccountConst.True) return { errorno: -1 }
      const {
        data: { errorno, errormsg, examineAllState, ...rest }
      } = await requestGetExamineStatus({})
      console.log('查看开发者资质审核状态', { errorno, errormsg, examineAllState, ...rest })
      if (errorno !== 0) {
        MessagePlugin('error', errormsg)
        return { errorno }
      }
      this.setExamineState(examineAllState)

      return { errorno, examineAllState, ...rest }
    },

    /**
     * 设置开发者登录信息
     */
    setDeveloperLoginInfo(developerInfo: DeveloperInfo) {
      const getDeveloperPwd = () => {
        return developerInfo.isSubDeveloper === IsSubAccountConst.True
          ? 'P92zslPKSP208A9Ss1KQ1lka1n1SSD'
          : developerInfo.developerPwd ?? ''
      }
      this.developerLoginInfo = {
        isSubDeveloper: developerInfo.isSubDeveloper,
        developerId: developerInfo.developerId,
        accountPower: developerInfo.accountPower,
        developerMobile: developerInfo.developerMobile,
        developerEmail: developerInfo.developerEmail ?? '',
        developerPwd: getDeveloperPwd(),
        examineAllState: this.developerLoginInfo.examineAllState
      }
    },
    /**
     * 设置开发者联系人信息
     */
    setDeveloperContactInfo(developerInfo: DeveloperInfo) {
      this.developerContactInfo = {
        developerName: developerInfo.developerName,
        contactPersonEmail: developerInfo.contactPersonEmail,
        identityCardNum: developerInfo.identityCardNum,
        identityCardPic: [developerInfo.identityCardPicFront, developerInfo.identityCardPicVerso],
        developerAddress: developerInfo.developerAddress,
        developerAddInfo: developerInfo.developerAddInfo
      }
    },
    /**
     * 设置资质审核状态
     */
    setExamineState(examineAllState: number) {
      this.developerLoginInfo = {
        ...this.developerLoginInfo,
        examineAllState
      }
    },

    /**
     * 获取开发者头像
     */
    getDeveloperAvatar(str: string) {
      const avatarColorList = Object.values(DeveloperAvatarBgColor)
      const avatarBgColor = avatarColorList[getRandomNum(0, avatarColorList.length - 1)]

      if (window.isInternational) {
        return this.developerLoginInfo.examineAllState !== CertificationStatus.AuditSuccess
          ? { avatarText: '', avatarBg: UnverifiedAvatar, avatarBgColor: 'transparent' }
          : {
              avatarText: str.slice(0, 2),
              avatarBg: '',
              avatarBgColor: this.avatarBgColor !== 'transparent' ? this.avatarBgColor : avatarBgColor
            }
      } else {
        return {
          avatarText: str.slice(-4),
          avatarBg: '',
          avatarBgColor: this.avatarBgColor !== 'transparent' ? this.avatarBgColor : avatarBgColor
        }
      }
    },
    /**
     * 设置开发者头像
     */
    setDeveloperAvatar() {
      const certificationInfoStore = useCertificationInfoStore()
      const { developerMobile } = this.developerLoginInfo

      this.developerAvatar = this.getDeveloperAvatar(
        window.isInternational ? certificationInfoStore.companyQualifications.companyName : developerMobile
      )
    },
    /**
     * 清空数据
     */
    clearDeveloperData() {
      this.developerLoginInfo = {
        isSubDeveloper: 0,
        developerId: '',
        accountPower: [],
        developerMobile: '',
        developerEmail: '',
        developerPwd: '',
        examineAllState: 0 // 资质认证审核总状态
      }
      this.developerAvatar = { avatarText: '', avatarBg: '', avatarBgColor: 'transparent' }
      this.developerContactInfo = {
        developerName: '', // 联系人姓名
        contactPersonEmail: '', // 联系人邮箱
        identityCardNum: '', // 联系人身份证号
        identityCardPic: [], // 联系人身份证照片
        developerAddress: '', //开发者地址省市
        developerAddInfo: '' //开发者详细地址
      }
    },
    /**
     * 退出登录
     */
    async logout() {
      const certificationInfoStore = useCertificationInfoStore()
      await requestLogout({})

      this.clearDeveloperData()
      certificationInfoStore.clearCertificationInfoData()

      return true
    },
    /**
     * 登录过期
     */
    loginExpired() {
      const certificationInfoStore = useCertificationInfoStore()
      this.clearDeveloperData()
      certificationInfoStore.clearCertificationInfoData()
      mittBus.emit('loginExpired')
    }
  },
  persist: true
})

export default useDeveloperStore
